import React, { useState, useRef, useEffect, Component, ErrorInfo, ReactNode, lazy, Suspense } from 'react';
import { TierProvider, useTier } from './context/TierContext';
import { PoolProvider } from './context/PoolContext';
import { LanguageProvider, useT } from './i18n';
import NavSidebar from './components/NavSidebar';

const Dashboard    = lazy(() => import('./pages/Dashboard'));
const PricingPage  = lazy(() => import('./pages/PricingPage'));
const OpeningPage  = lazy(() => import('./pages/OpeningPage'));
const PositiePage  = lazy(() => import('./pages/PositiePage'));
const EindspelPage = lazy(() => import('./pages/EindspelPage'));
const AccuracyAuditPage = lazy(() => import('./pages/AccuracyAuditPage'));
const InfoPage          = lazy(() => import('./pages/InfoPage'));

function PageLoader() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        color: 'rgba(255,255,255,0.18)',
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 11,
        letterSpacing: 3,
      }}
    >
      LADEN...
    </div>
  );
}

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: 40,
            color: '#EF4444',
            fontFamily: 'JetBrains Mono, monospace',
            whiteSpace: 'pre-wrap',
            fontSize: 13,
            maxWidth: '90vw',
            overflow: 'auto',
            background: '#080810',
            minHeight: '100vh',
          }}
        >
          <h2 style={{ color: 'rgba(255,255,255,0.65)', marginBottom: 16, fontWeight: 300, letterSpacing: 4 }}>SYSTEM ERROR</h2>
          <p style={{ color: 'rgba(255,255,255,0.55)', marginBottom: 12 }}>Er ging iets mis. Herlaad de pagina.</p>
          <pre style={{ color: 'rgba(255,255,255,0.40)', background: 'rgba(255,255,255,0.04)', padding: 16, borderRadius: 8, border: 'none' }}>
            {this.state.error?.stack || this.state.error?.message || 'Unknown error'}
          </pre>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 20,
              padding: '10px 24px',
              background: 'rgba(255,255,255,0.06)',
              color: 'rgba(255,255,255,0.65)',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 12,
              fontFamily: 'JetBrains Mono, monospace',
              letterSpacing: 2,
            }}
          >
            HERSTART
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

class PageErrorBoundary extends Component<
  { children: ReactNode; pageKey: string; onBack: () => void },
  { hasError: boolean; error: Error | null; errorPage: string }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null, errorPage: '' };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  static getDerivedStateFromProps(props: any, state: any) {
    if (state.hasError && props.pageKey !== state.errorPage) {
      return { hasError: false, error: null, errorPage: '' };
    }
    return { errorPage: props.pageKey };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[PageError]', this.props.pageKey, error, info);
    try {
      const payload = JSON.stringify({
        page: this.props.pageKey,
        msg: (error?.message || 'unknown').slice(0, 800),
        stack: (error?.stack || '').slice(0, 1500),
        comp: (info?.componentStack || '').slice(0, 1500),
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/client-error', new Blob([payload], { type: 'application/json' }));
      }
    } catch {}
  }
  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: 32,
            color: 'rgba(255,255,255,0.72)',
            fontFamily: 'var(--font-sans, system-ui)',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 16,
          }}
        >
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.30)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </svg>
          <h3 style={{ color: 'rgba(255,255,255,0.45)', fontWeight: 400, letterSpacing: 2, fontSize: 14, margin: 0 }}>PAGINA FOUT</h3>
          <p style={{ color: 'rgba(255,255,255,0.50)', fontSize: 13, maxWidth: 300 }}>
            Deze pagina kon niet geladen worden. Probeer het opnieuw of ga terug.
          </p>
          <p data-testid="error-detail" style={{ color: 'rgba(255,255,255,0.25)', fontSize: 10, maxWidth: 500, wordBreak: 'break-all', fontFamily: 'monospace' }}>
            {String(this.state.error?.message || '').slice(0, 300)}
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              style={{ padding: '8px 20px', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.65)', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, letterSpacing: 1 }}
            >
              OPNIEUW
            </button>
            <button
              onClick={this.props.onBack}
              style={{ padding: '8px 20px', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.42)', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, letterSpacing: 1 }}
            >
              DASHBOARD
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

type Page = 'dashboard' | 'opening' | 'positie' | 'eindspel' | 'info' | 'pricing' | 'betaling-succes' | 'accuracy-audit';

const PAGE_ORDER: Page[] = ['dashboard', 'opening', 'positie', 'eindspel', 'info', 'pricing'];

function PaymentSuccess({ onNavigate }: { onNavigate: (p: Page) => void }) {
  const { setTier } = useTier();
  const t = useT();
  const params = new URLSearchParams(window.location.search);
  const plan = params.get('plan') || 'premium';

  useEffect(() => {
    if (plan === 'light' || plan === 'premium') setTier(plan);
    const timer = setTimeout(() => {
      window.history.replaceState({}, '', '/');
      onNavigate('dashboard');
    }, 4000);
    return () => clearTimeout(timer);
  }, [plan, setTier, onNavigate]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg)', gap: 16 }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%',
        background: 'rgba(16,185,129,0.12)',
        border: '1px solid rgba(16,185,129,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22, color: '#10b981',
      }}>
        &#10003;
      </div>
      <h2 style={{ color: 'rgba(255,255,255,0.88)', fontSize: 18, fontWeight: 300, letterSpacing: 4, margin: 0, fontFamily: '-apple-system, Inter, sans-serif' }}>
        {t('page.betalingGeslaagd')}
      </h2>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, letterSpacing: 0.4 }}>
        Je {plan === 'premium' ? t('tier.institutioneel') : t('tier.analist')} {t('page.abonnementActief')}
      </p>
    </div>
  );
}

function AppContent() {
  const initialPage = (): Page => {
    if (typeof window !== 'undefined' && window.location.pathname.includes('betaling/succes')) return 'betaling-succes';
    return 'dashboard';
  };

  const [page, setPage] = useState<Page>(initialPage);
  const [slideDir, setSlideDir] = useState<'none' | 'left' | 'right'>('none');
  const [animating, setAnimating] = useState(false);
  const [displayPage, setDisplayPage] = useState<Page>(page);
  const mainRef = useRef<HTMLDivElement>(null);
  const animTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navigate = (target: Page) => {
    if (target === page || animating) return;
    const fromIdx = PAGE_ORDER.indexOf(page);
    const toIdx = PAGE_ORDER.indexOf(target);
    const dir = toIdx > fromIdx ? 'left' : 'right';
    setSlideDir(dir);
    setAnimating(true);

    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    animTimerRef.current = setTimeout(() => {
      setAnimating(false);
      setSlideDir('none');
    }, 1200);

    setTimeout(() => {
      setPage(target);
      setDisplayPage(target);
      setSlideDir(dir === 'left' ? 'right' : 'left');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSlideDir('none');
          setTimeout(() => {
            setAnimating(false);
            if (animTimerRef.current) {
              clearTimeout(animTimerRef.current);
              animTimerRef.current = null;
            }
          }, 300);
        });
      });
    }, 150);
  };

  const renderPage = () => {
    switch (displayPage) {
      case 'pricing':         return <PricingPage onNavigate={navigate} />;
      case 'opening':         return <OpeningPage onNavigate={navigate} />;
      case 'positie':         return <PositiePage />;
      case 'eindspel':        return <EindspelPage />;
      case 'info':            return <InfoPage />;
      case 'accuracy-audit':
        return <AccuracyAuditPage />;
      case 'betaling-succes': return <PaymentSuccess onNavigate={navigate} />;
      default:                return <Dashboard onNavigate={navigate} />;
    }
  };

  const slideClass = slideDir === 'left' ? 'slide-exit-left' : slideDir === 'right' ? 'slide-enter-right' : 'slide-center';

  return (
    <div className="app-layout">
      <NavSidebar currentPage={page} onNavigate={(p: Page) => navigate(p)} />
      <main ref={mainRef} className={`app-main ${slideClass}`}>
        <PageErrorBoundary pageKey={displayPage} onBack={() => navigate('dashboard')}>
          <Suspense fallback={<PageLoader />}>
            {renderPage()}
          </Suspense>
        </PageErrorBoundary>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <TierProvider>
          <PoolProvider>
            <AppContent />
          </PoolProvider>
        </TierProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}
